<script lang="ts">
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { FormError, FormFieldInjectedOptions } from '#ui/types';
import { tv } from 'tailwind-variants';
import { injectFormContext } from './Form.vue';

const formField = tv({
	slots: {
		root: 'col-span-full',
		inlineWrapper: 'grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1',
		labelWrapper: 'group relative inline-flex w-fit items-center',
		label: 'font-medium text-default',
		description: 'text-muted',
		error: 'font-medium text-error',
		help: 'text-xs text-muted',
	},
	variants: {
		size: {
			sm: {
				label: 'text-xs',
				description: 'text-2xs',
				error: 'text-2xs',
			},
			md: {
				label: 'text-sm',
				description: 'text-xs',
				error: 'text-xs',
			},
			lg: {
				label: 'text-sm',
				description: 'text-sm',
				error: 'text-sm',
			},
		},
	},
	defaultVariants: {
		size: 'md',
	},
});

export interface FormFieldProps {
	/** The name of the FormField. Also used to match form errors. */
	name?: string;

	label?: string;
	description?: string;
	help?: string;
	error?: string | boolean;
	errorPattern?: RegExp;
	hint?: string;

	size?: 'sm' | 'md' | 'lg';

	required?: boolean;

	/** If true, validation on input will be active immediately instead of waiting for a blur event. */
	eagerValidation?: boolean;
	/**
	 * Delay in milliseconds before validating the form on input events.
	 * @defaultValue `300`
	 */
	validateOnInputDelay?: number;

	layout?: 'side-by-side' | 'stacked' | 'inline';
	layoutSplit?: '1-2' | '1-1';

	/**
	 * Vertically center the label in the `side-by-side` layout.
	 */
	centerLabel?: boolean;

	hideLabel?: boolean;

	class?: any;
	ui?: ThemeSlots<typeof formField>;
}

export interface FormFieldSlots {
	'label': (props: { label?: string }) => any;
	'label-trailing': () => any;
	'hint': (props: { hint?: string }) => any;
	'description': (props: { description?: string }) => any;
	'help': (props: { help?: string }) => any;
	'error': (props: { error?: string | boolean }) => any;
	'default': (props: { error?: string | boolean }) => any;
	'content-top': () => any;
	'content-bottom': () => any;
}
</script>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue';
import type { FormContext } from './Form.vue';
import { createReusableTemplate } from '@vueuse/core';
import { Label, VisuallyHidden } from 'reka-ui';
import { computed, inject, provide, ref, useId, watch } from 'vue';
import { useTranslationUtils } from '#layers/i18n/public';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import { formErrorsInjectionKey, formFieldInjectionKey, formInputsInjectionKey, inputIdInjectionKey } from '#ui/composables/useFormField.js';

const props = defineProps<FormFieldProps>();
const slots = defineSlots<FormFieldSlots>();

const formContext = injectFormContext(null);
// eslint-disable-next-line vue/no-dupe-keys
const { layout, layoutSplit, size } = useFormContext();
const ui = computed(() => mergeUi(props.ui, formContext?.fieldUi.value));

const theme = computed(() =>
	formField({
		size: size.value,
	}),
);

const { translateString } = useTranslationUtils();

const formErrors = inject<Ref<FormError[]> | null>(formErrorsInjectionKey, null);

const error = computed(() => {
	const err = props.error || formErrors?.value?.find((error) => error.name && (error.name === props.name || (props.errorPattern && error.name.match(props.errorPattern))))?.message;
	if (typeof err === 'string') return translateString(err);
	return err;
});

const id = ref(useId());
// Copies id's initial value to bind aria-attributes such as aria-describedby.
// This is required for the RadioGroup component which unsets the id value.
const ariaId = id.value;

const formInputs = inject(formInputsInjectionKey);

watch(id, () => {
	if (formInputs && props.name) {
		formInputs.value[props.name] = { id: id.value, pattern: props.errorPattern };
	}
}, { immediate: true });

provide(inputIdInjectionKey, id);

provide(formFieldInjectionKey, computed(() => ({
	error: error.value,
	name: props.name,
	size: size.value,
	eagerValidation: props.eagerValidation,
	validateOnInputDelay: props.validateOnInputDelay,
	errorPattern: props.errorPattern,
	hint: props.hint,
	description: props.description,
	help: props.help,
	ariaId,
}) as FormFieldInjectedOptions<FormFieldProps>));

const [DefineLabel, ReuseLabel] = createReusableTemplate();
const [DefineLabelImpl, ReuseLabelImpl] = createReusableTemplate();
const [DefineDescription, ReuseDescription] = createReusableTemplate();
const [DefineControl, ReuseControl] = createReusableTemplate();
const [DefineHelp, ReuseHelp] = createReusableTemplate();

function useFormContext() {
	const formContext = injectFormContext(null);

	return computedFromPropsOrContext(props, 'layout', 'layoutSplit', 'size');

	function computedFromPropsOrContext<K extends keyof FormContext & keyof FormFieldProps>(
		props: FormFieldProps,
		...keys: K[]
	): { [P in K]: ComputedRef<FormFieldProps[P]> } {
		return keys.reduce((acc, key) => {
			acc[key] = computed(() => props[key] ?? formContext?.[key]?.value as FormFieldProps[K]);
			return acc;
		}, {} as { [P in K]: ComputedRef<FormFieldProps[P]> });
	}
}
</script>

<template>
	<DefineLabelImpl>
		<div
			v-if="label || !!slots.label || !!slots['label-trailing']"
			:class="theme.labelWrapper({ class: [props.ui?.labelWrapper] })"
		>
			<Label v-if="label || !!slots.label" :for="id" :class="theme.label({ class: ui?.label })">
				<slot name="label" :label>
					<DFormFieldLabel v-if="label" :label :required />
				</slot>
			</Label>
			<!-- Trailing slot for actions/buttons that should NOT be inside label -->
			<slot name="label-trailing" />
		</div>
	</DefineLabelImpl>

	<DefineLabel>
		<VisuallyHidden v-if="hideLabel" as-child>
			<ReuseLabelImpl />
		</VisuallyHidden>
		<ReuseLabelImpl v-else />
	</DefineLabel>

	<DefineDescription>
		<p
			v-if="description || !!slots.description"
			:id="`${ariaId}-description`"
			:class="theme.description({ class: ui?.description })"
		>
			<slot name="description" :description>
				{{ description }}
			</slot>
		</p>
	</DefineDescription>

	<DefineControl>
		<div class="@container flex h-full flex-col gap-1 *:w-full">
			<slot :error />
			<p
				v-if="(typeof error === 'string' && error) || !!slots.error"
				:id="`${ariaId}-error`"
				:class="theme.error({ class: ui?.error })"
			>
				<slot name="error" :error>
					{{ error }}
				</slot>
			</p>
		</div>
	</DefineControl>

	<DefineHelp>
		<p
			v-if="help || !!slots.help"
			:id="`${ariaId}-help`"
			:class="theme.help({ class: ui?.help })"
		>
			<slot name="help" :help>
				{{ help }}
			</slot>
		</p>
	</DefineHelp>

	<DGrid
		:class="theme.root({ class: [ui?.root, props.class] })"
		v-bind="$attrs"
	>
		<template v-if="layout === 'side-by-side'">
			<DGrid
				:class="{
					'col-span-4': layoutSplit === '1-2',
					'flex flex-col justify-center': centerLabel,
				}"
			>
				<ReuseLabel />
				<ReuseDescription />
			</DGrid>
			<DGrid
				class="*:w-full"
				:class="{
					'col-span-8': layoutSplit === '1-2',
					'flex flex-col justify-center': centerLabel,
				}"
			>
				<ReuseControl />
				<ReuseHelp />
			</DGrid>
		</template>
		<template v-else-if="layout === 'inline'">
			<div :class="theme.inlineWrapper({ class: ui?.inlineWrapper })">
				<ReuseLabel />
				<ReuseControl class="w-full" />
				<ReuseDescription class="col-start-2" />
				<ReuseHelp class="col-start-2" />
			</div>
		</template>
		<div v-else class="flex flex-col gap-2">
			<slot name="content-top" />
			<ReuseLabel />
			<div class="*:w-full">
				<ReuseControl />
			</div>
			<ReuseDescription />
			<ReuseHelp />
			<slot name="content-bottom" />
		</div>
	</DGrid>
</template>
